import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Divider,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Switch,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import API from './Api';
import PenaltySettings from './PenaltySettings';
import ManualProcessDialog from './ManualProcessDialog';

// ----------------- API Configurations Component -----------------
export const ApiConfigurations = ({ showNotification }) => {
    const [isEditingAPI, setIsEditingAPI] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // API Configuration state
    const [apiConfigs, setApiConfigs] = useState([]);
  
    // New API form state
    const [newApiConfig, setNewApiConfig] = useState({
      name: '',
      fields: [{ name: 'API Key', value: '', visible: false }]
    });
  
    // Store original states when editing starts
    const [originalApiConfigs, setOriginalApiConfigs] = useState([]);

    useEffect(() => {
        fetchApiConfigurations();
      }, []);
    
      const fetchApiConfigurations = async () => {
        try {
          setLoading(true);
          const response = await API.company.getApiConfigurations();
          
          // Transform the response data to match our component's state format
          const transformedConfigs = response.api_configurations.map(config => ({
            id: config.id,
            name: config.name,
            fields: config.fields.map(field => ({
              name: field.name,
              value: field.value,
              visible: false
            }))
          }));
          
          setApiConfigs(transformedConfigs);
          setError(null);
        } catch (err) {
          setError('Failed to load API configurations');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      const handleEditAPI = () => {
        setOriginalApiConfigs(JSON.parse(JSON.stringify(apiConfigs)));
        setIsEditingAPI(true);
      };
    
      const handleSaveAPISettings = async () => {
        try {
          // For each updated config, send an update request
          for (const config of apiConfigs) {
            const originalConfig = originalApiConfigs.find(c => c.id === config.id);
            
            // Skip if no changes
            if (JSON.stringify(config) === JSON.stringify(originalConfig)) {
              continue;
            }
            
            // Prepare data for update
            const updateData = {
              name: config.name,
              fields: config.fields.map(field => ({
                name: field.name,
                value: field.value
              }))
            };
            
            await API.company.updateApiConfiguration(config.id, updateData);
          }
          
          setIsEditingAPI(false);
          showNotification('API configurations saved successfully');
        } catch (err) {
          showNotification('Failed to save API configurations', 'error');
          console.error(err);
        }
      };

  const handleCancelAPI = () => {
    setApiConfigs(JSON.parse(JSON.stringify(originalApiConfigs)));
    setIsEditingAPI(false);
  };

  const handleApiVisibility = (configIndex, fieldIndex) => {
    const updatedApiConfigs = [...apiConfigs];
    updatedApiConfigs[configIndex].fields[fieldIndex].visible = 
      !updatedApiConfigs[configIndex].fields[fieldIndex].visible;
    setApiConfigs(updatedApiConfigs);
  };

  const handleNewApiFieldChange = (index, field) => (event) => {
    const updatedFields = [...newApiConfig.fields];
    updatedFields[index][field] = event.target.value;
    setNewApiConfig({
      ...newApiConfig,
      fields: updatedFields
    });
  };

  const handleNewApiNameChange = (event) => {
    setNewApiConfig({
      ...newApiConfig,
      name: event.target.value
    });
  };

  const handleAddField = () => {
    setNewApiConfig({
      ...newApiConfig,
      fields: [...newApiConfig.fields, { name: '', value: '', visible: false }]
    });
  };

  const handleRemoveField = (index) => {
    const updatedFields = newApiConfig.fields.filter((_, i) => i !== index);
    setNewApiConfig({
      ...newApiConfig,
      fields: updatedFields
    });
  };

  const toggleFieldVisibility = (index) => {
    const updatedFields = [...newApiConfig.fields];
    updatedFields[index].visible = !updatedFields[index].visible;
    setNewApiConfig({
      ...newApiConfig,
      fields: updatedFields
    });
  };

  const handleAddApiConfig = async () => {
    // Validate that all fields have values
    const isValid = newApiConfig.name && 
                   newApiConfig.fields.length > 0 &&
                   newApiConfig.fields.every(field => field.name && field.value);
    
    if (isValid) {
      try {
        // Prepare data for API
        const configData = {
          name: newApiConfig.name,
          fields: newApiConfig.fields.map(field => ({
            name: field.name,
            value: field.value
          }))
        };
        
        // Create the new API configuration
        const response = await API.company.createApiConfiguration(configData);
        
        // Add to local state
        setApiConfigs([
          ...apiConfigs,
          {
            id: response.id,
            name: newApiConfig.name,
            fields: newApiConfig.fields.map(field => ({
              name: field.name,
              value: field.value,
              visible: false
            }))
          }
        ]);
        
        // Reset the form
        setNewApiConfig({
          name: '',
          fields: [{ name: 'API Key', value: '', visible: false }]
        });
        setOpenAddDialog(false);
        
        showNotification('API configuration added successfully');
      } catch (err) {
        showNotification('Failed to add API configuration', 'error');
        console.error(err);
      }
    }
  };

  const handleDeleteApiConfig = async (id) => {
    try {
      await API.company.deleteApiConfiguration(id);
      setApiConfigs(apiConfigs.filter(config => config.id !== id));
      showNotification('API configuration deleted successfully');
    } catch (err) {
      showNotification('Failed to delete API configuration', 'error');
      console.error(err);
    }
  };

  // Function to mask API keys
  const maskApiValue = (value, visible) => {
    if (visible) return value;
    if (value.length <= 8) return '•'.repeat(value.length);
    return `${value.substring(0, 4)}${'•'.repeat(value.length - 8)}${value.substring(value.length - 4)}`;
  };

  if (loading) {
    return <Typography>Loading API configurations...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ color: '#1976d2' }}>API Configurations</Typography>
        {!isEditingAPI ? (
          <Button 
            variant="outlined" 
            startIcon={<EditIcon />} 
            onClick={handleEditAPI}
          >
            Edit
          </Button>
        ) : null}
      </Box>

      <Paper elevation={isEditingAPI ? 2 : 0} sx={{ p: isEditingAPI ? 3 : 0, mb: 4 }}>
        {apiConfigs.length === 0 ? (
          <Typography variant="body1" sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
            No API configurations added yet.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {apiConfigs.map((config, configIndex) => (
              <Grid item xs={12} key={config.id}>
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  bgcolor: '#f8f9fa'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="medium">{config.name}</Typography>
                    {isEditingAPI && (
                      <IconButton color="error" size="small" onClick={() => handleDeleteApiConfig(config.id)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {config.fields.map((field, fieldIndex) => (
                      <Box key={fieldIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={field.name} 
                          size="small" 
                          sx={{ 
                            bgcolor: '#e91e63', 
                            color: 'white',
                            width: '100px',
                            justifyContent: 'flex-start'
                          }} 
                        />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                          {maskApiValue(field.value, field.visible)}
                        </Typography>
                        {isEditingAPI && (
                          <IconButton 
                            size="small" 
                            onClick={() => handleApiVisibility(configIndex, fieldIndex)}
                          >
                            {field.visible 
                              ? <VisibilityOffIcon fontSize="small" /> 
                              : <VisibilityIcon fontSize="small" />
                            }
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {isEditingAPI && (
          <>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddDialog(true)}
                sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
              >
                Add New API Configuration
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancelAPI}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveAPISettings}
                color="primary"
              >
                Save API Settings
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Dialog for adding new API configuration */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add New API Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <TextField
              fullWidth
              label="API Name"
              value={newApiConfig.name}
              onChange={handleNewApiNameChange}
              variant="outlined"
              margin="normal"
            />
            
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>API Fields</Typography>
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              {newApiConfig.fields.map((field, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Field Name"
                      value={field.name}
                      onChange={handleNewApiFieldChange(index, 'name')}
                      variant="outlined"
                      placeholder="e.g. API Key, Consumer Secret"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Value"
                      value={field.value}
                      onChange={handleNewApiFieldChange(index, 'value')}
                      variant="outlined"
                      type={field.visible ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => toggleFieldVisibility(index)}
                              edge="end"
                            >
                              {field.visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {index > 0 && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveField(index)}
                          fullWidth
                        >
                          <DeleteIcon fontSize="small" />
                        </Button>
                      )}
                      {index === newApiConfig.fields.length - 1 && (
                        <Button
                          variant="outlined"
                          color="success"
                          onClick={handleAddField}
                          fullWidth
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddApiConfig} 
            variant="contained" 
            color="primary"
            disabled={!newApiConfig.name || newApiConfig.fields.some(f => !f.name || !f.value)}
          >
            Add Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ----------------- Payment Schedule Settings Component -----------------
export const PaymentScheduleSettings = ({ showNotification }) => {
    const [isEditingPayment, setIsEditingPayment] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingCharges, setProcessingCharges] = useState(false);
    const [showProcessingDialog, setShowProcessingDialog] = useState(false);
    
    // Get current date information
    const currentDate = new Date();
    const currentDay = currentDate.getDate();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
  
    // Payment Schedule state
    const [paymentSchedule, setPaymentSchedule] = useState({
      monthlyDeductionDay: 5,
      gracePeriodDays: 0,
      penaltyType: 'percentage',
      penaltyValue: '',
      breakdownOrder: [],
    });
  
    // Fetch company profile to get payment schedule on component mount
    useEffect(() => {
      fetchCompanyProfile();
    }, []);
  
    const fetchCompanyProfile = async () => {
      try {
        setLoading(true);
        const response = await API.company.getCompanyProfile();
        
        setPaymentSchedule({
          monthlyDeductionDay: response.company.payment_deduction_day || 5,
          gracePeriodDays:     response.company.grace_period_days ?? 0,
          penaltyType:         response.company.penalty_type || 'percentage',
          penaltyValue:        response.company.penalty_value ?? '',
          breakdownOrder:      response.company.breakdown_settlement_order || [],
        });
        
        setError(null);
      } catch (err) {
        setError('Failed to load payment schedule');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    // Calculate next deduction date
    const calculateNextDeductionDate = () => {
      let nextDeductionDate = new Date(currentYear, currentMonth, paymentSchedule.monthlyDeductionDay);
      
      // If today is after the deduction day this month, move to next month
      if (currentDay > paymentSchedule.monthlyDeductionDay) {
        nextDeductionDate = new Date(currentYear, currentMonth + 1, paymentSchedule.monthlyDeductionDay);
      }
      
      return nextDeductionDate;
    };
  
    const nextDeductionDate = calculateNextDeductionDate();
    const isDeductionDone = currentDay > paymentSchedule.monthlyDeductionDay;
    
    // Format date to string
    const formatDate = (date) => {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString(undefined, options);
    };
  
    // Store original states when editing starts
    const [originalPaymentSchedule, setOriginalPaymentSchedule] = useState({...paymentSchedule});
  
    const handleEditPayment = () => {
      setOriginalPaymentSchedule({...paymentSchedule});
      setIsEditingPayment(true);
    };
  
    const handleSavePaymentSettings = async () => {
      try {
        const scheduleData = {
          payment_deduction_day: paymentSchedule.monthlyDeductionDay,
          grace_period_days: paymentSchedule.gracePeriodDays,
          penalty_type: paymentSchedule.penaltyType,
          penalty_value: paymentSchedule.penaltyValue === '' ? 0 : parseFloat(paymentSchedule.penaltyValue)
        };
        
        await API.company.updatePaymentSchedule(scheduleData);
        setIsEditingPayment(false);
        showNotification('Payment schedule saved successfully');
      } catch (err) {
        showNotification('Failed to save payment schedule', 'error');
        console.error(err);
      }
    };
  
    const handleCancelPayment = () => {
      setPaymentSchedule({...originalPaymentSchedule});
      setIsEditingPayment(false);
    };
  
    const handlePaymentScheduleChange = (field) => (event) => {
      setPaymentSchedule({
        ...paymentSchedule,
        [field]: event.target.value
      });
    };
  
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
  
    if (error) {
      return <Typography color="error">{error}</Typography>;
    }
  
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ color: '#1976d2' }}>Payment Schedule Settings</Typography>
          {!isEditingPayment ? (
            <Button 
              variant="outlined" 
              startIcon={<EditIcon />} 
              onClick={handleEditPayment}
            >
              Edit
            </Button>
          ) : null}
        </Box>
  
        <Paper elevation={isEditingPayment ? 2 : 0} sx={{ p: isEditingPayment ? 3 : 2 }}>
          <Grid container spacing={3} alignItems="flex-start">

            {/* ── Row 1: Processing date + deduction status ── */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" disabled={!isEditingPayment}>
                <InputLabel id="monthly-deduction-label">Monthly Processing Date</InputLabel>
                <Select
                  labelId="monthly-deduction-label"
                  value={paymentSchedule.monthlyDeductionDay}
                  label="Monthly Processing Date"
                  onChange={handlePaymentScheduleChange('monthlyDeductionDay')}
                  readOnly={!isEditingPayment}
                >
                  {[...Array(28)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} of each month
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: '#f5f5f5'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarMonthIcon sx={{ mr: 1, color: '#4caf50' }} />
                  <Typography variant="subtitle1">Deduction Status</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Current Month Status:</Typography>
                    <Chip
                      label={isDeductionDone ? "Completed" : "Pending"}
                      size="small"
                      color={isDeductionDone ? "success" : "warning"}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Next Deduction Date:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(nextDeductionDate)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* ── Row 2: Grace period ── */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }}>
                <Chip label="Late Payment Rules" size="small" />
              </Divider>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" disabled={!isEditingPayment}>
                <InputLabel id="grace-period-label">Grace Period</InputLabel>
                <Select
                  labelId="grace-period-label"
                  value={paymentSchedule.gracePeriodDays}
                  label="Grace Period"
                  onChange={handlePaymentScheduleChange('gracePeriodDays')}
                  readOnly={!isEditingPayment}
                >
                  <MenuItem value={0}>No grace period</MenuItem>
                  {[...Array(30)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1} day{i > 0 ? 's' : ''} after processing date
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                How long after the processing date before a penalty can be imposed on unpaid rent.
              </Typography>
            </Grid>

            {/* ── Row 3: Penalty settings (child component) ── */}
            <Grid item xs={12}>
              <PenaltySettings
                penaltyType={paymentSchedule.penaltyType}
                penaltyValue={paymentSchedule.penaltyValue}
                isEditing={isEditingPayment}
                onTypeChange={handlePaymentScheduleChange('penaltyType')}
                onValueChange={handlePaymentScheduleChange('penaltyValue')}
              />
            </Grid>

            {/* ── Manual processing ── */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                p: 2,
                border: '1px dashed', 
                borderColor: 'primary.main',
                borderRadius: 1,
                bgcolor: '#f8f9fa'
              }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setShowProcessingDialog(true)}
                  disabled={processingCharges}
                  startIcon={processingCharges ? <CircularProgress size={20} /> : <CalendarMonthIcon />}
                  sx={{ 
                    bgcolor: '#ff9800', 
                    '&:hover': { bgcolor: '#f57c00' },
                    fontWeight: 'bold'
                  }}
                >
                  {processingCharges ? 'Processing...' : 'Manually Process Monthly Charges'}
                </Button>
              </Box>
            </Grid>

            {isEditingPayment && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancelPayment}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSavePaymentSettings}
                    color="primary"
                  >
                    Save Payment Settings
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
            <ManualProcessDialog
      open={showProcessingDialog}
      onClose={() => setShowProcessingDialog(false)}
      showNotification={showNotification}
      currentOrder={paymentSchedule.breakdownOrder}
    />
      </Box>
    );
  };